import { type Callback, type Optional, call } from "shelving";
import { useMount } from "shelving/react";

/**
 * Fires a callback when an element enters the visible scrolling area on the client.
 * - Checks that is the last element in its parent.
 * - Finds the closest parent above this that scrolls (e.g. `.page`, customisable with `selector` param), then fires the callbacks appropriately.
 */
export function useScrollIntersect<T extends HTMLElement>(onEnter?: Optional<Callback>, onLeave?: Optional<Callback>): React.RefCallback<T> {
	return useMount(_mountScrollIntersect, onEnter, onLeave);
}

function _mountScrollIntersect<T extends HTMLElement>(el: T, onEnter?: Optional<Callback>, onLeave?: Optional<Callback>) {
	const observer = new IntersectionObserver(
		([entry]) => {
			if (entry) {
				if (entry.isIntersecting) {
					if (onEnter) call(onEnter);
				} else {
					if (onLeave) call(onLeave);
				}
			}
		},
		{ threshold: [1] },
	);
	observer.observe(el);
	return () => observer.disconnect();
}

/** Go through a list of `IntersectionObserverEntry` (from `IntersectionObserver`) and return the index of the one with the highest `intersectionRatio` */
export function getMostVisibleObserverEntry(entries: IntersectionObserverEntry[]): IntersectionObserverEntry | undefined {
	return entries.reduce<IntersectionObserverEntry | undefined>(_reduceMostVisibleObserverEntry, undefined);
}
function _reduceMostVisibleObserverEntry(mostVisibleEntry: IntersectionObserverEntry | undefined, currentEntry: IntersectionObserverEntry): IntersectionObserverEntry | undefined {
	return mostVisibleEntry && mostVisibleEntry.intersectionRatio > currentEntry.intersectionRatio ? mostVisibleEntry : currentEntry;
}
